import { capacityDescription, CURRENT_STATE_PROJECT_KEY, INSIGHTS_FIELD, getActionFrequencyTypeShortName, getDurationDescription, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, NODE_WARNING, usesCalculatedFrequency, WORK_TYPE, WORK_TYPE_NAMES } from "../../common/linqset-model"
import { InsightsFieldType } from "../../insights/model/insights-field-type"
import { ACTION_TYPE, ModelDetails, Tag } from "../../common/organisation"
import { NODE_TYPE_DATA_TYPE } from "../node-types/node-type-data"
import { USER_ROLE } from "../../common/user"

export class NodeRowData {
    public insightsValues = {}
    public change:string
    public role:string
    public system:string
    public model:ModelDetails
    public modelNames:string[]
    public from:string              // for inputs where did it come from external or another model
    public asset:string             // for info node, the name of the asset
    public icon:string              // if want to set a specific icon like a tag
    public tags:Tag[] = []

    constructor(public node:LinqsetNode, public modelKey:string, public insights:any, dataType:NODE_TYPE_DATA_TYPE, columns:InsightsFieldType[], public warnings:NODE_WARNING[], public information:string[], public comparisonInsights?:any) {
        const before = comparisonInsights && comparisonInsights.projectKey === CURRENT_STATE_PROJECT_KEY?comparisonInsights:insights
        const after = comparisonInsights?(comparisonInsights.projectKey === CURRENT_STATE_PROJECT_KEY?insights:comparisonInsights):undefined

        this.insightsValues = columns.reduce((m, c) => {
            m[c.type] = c.getSingleValueFromInsights(before, dataType)
            if (after) m[c.type.toString() + '2'] = c.getSingleValueFromInsights(after, dataType)
            return m
        }, {})
        // an external organisation's capacity is only taken to match its work, so it's left blank and out of the total
        if (node?.type === NODE_TYPE.EXTERNAL_ORGANISATION) {
            delete this.insightsValues[INSIGHTS_FIELD.CAPACITY]
            delete this.insightsValues[INSIGHTS_FIELD.CAPACITY + '2']
        }
    }

    get name():string { return this.node.name + (this.insights?.actionType === ACTION_TYPE.EXTERNAL?'*':'')}
    get description():string { return this.node.description}
    get _key():string { return this.node._key}
    get models():number { return this.modelNames?this.modelNames.length:0}
    get readonly():boolean {
        return !this.model?.role || this.model.role === USER_ROLE.READ
    }

    get tagKeys():string[] {
        return this.node.properties.tagKeys?Object.values(this.node.properties.tagKeys):[]
    }

    get notes():string {
        return this.node.notes && this.node.notes.length > 30?this.node.notes.substring(0, 30) + "...":this.node.notes
    }

    // the kind of work an action is: as the user said, or as LINQ read it
    get workType():string {
        if (this.node.superType !== NODE_SUPERTYPE.ACTION) return ""
        const kind = this.node.properties?.workType || this.insights?.workKind || WORK_TYPE.VALUE_ADDING
        return WORK_TYPE_NAMES[kind] + (this.node.properties?.workType ? "" : " (LINQ's read)")
    }

    get processingType():string {
        return (this.node.superType===NODE_SUPERTYPE.ACTION && usesCalculatedFrequency(this.node.properties))?getActionFrequencyTypeShortName(this.node.properties?.actionProcessType):""
    }

    // an external organisation's time and availability aren't the organisation's to set, so they're left blank
    private get external() {
        return this.node?.type === NODE_TYPE.EXTERNAL_ORGANISATION
    }

    get duration():string {
        if (this.external) return ""
        if (usesCalculatedFrequency(this.node.properties) && (this.node.superType === NODE_SUPERTYPE.ROLE || this.node.superType === NODE_SUPERTYPE.PEOPLE)) return "Default"

        return getDurationDescription(this.node.properties.duration)
    }

    get frequency():string { 
        if (!this.node || this.external || (this.node.superType !== NODE_SUPERTYPE.ROLE && this.node.superType !== NODE_SUPERTYPE.ACTION && this.node.superType !== NODE_SUPERTYPE.PEOPLE)) return ""
    
        if (this.node.superType === NODE_SUPERTYPE.ROLE) {
            if (this.insights?.hasPeople) return "Uses people"
        }
    
        if (usesCalculatedFrequency(this.node.properties)) {
            if (this.node.superType === NODE_SUPERTYPE.ACTION)
                return (this.insights?.isInput && this.insights?.numberProcessed === 0)?"Missing frequency":"Using input"
            else return "Default"
        }
    
        if (this.node.superType === NODE_SUPERTYPE.ACTION) {
            const frequency = this.node.properties?.frequency
            return frequency?.type ? `${frequency.ocurrences} each ${frequency.type.toString().toLowerCase()}` : ""
        }
        else return capacityDescription(this.node.properties)
    }
}
